import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLiveUpdate } from "@/contexts/LiveUpdateContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Plus, MoreHorizontal, Pencil, Trash2, Filter, AlertTriangle, Search, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import TaskDialog from "@/components/TaskDialog";

const STATUS_STYLES = {
  todo: "bg-slate-50 text-slate-800 border border-slate-300 dark:bg-slate-900 dark:text-slate-300 dark:border-slate-700",
  in_progress: "bg-blue-50 text-blue-800 border border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
  review: "bg-amber-50 text-amber-800 border border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800",
  done: "bg-emerald-50 text-emerald-800 border border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800",
};
const STATUS_LABELS = { todo: "To Do", in_progress: "In Progress", review: "Review", done: "Selesai" };
const PRIORITY_STYLES = {
  high: "bg-red-50 text-red-800 border border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800",
  medium: "bg-orange-50 text-orange-800 border border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800",
  low: "bg-blue-50 text-blue-800 border border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
};

export default function TasksPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterProject, setFilterProject] = useState("all");
  const [filterAssignee, setFilterAssignee] = useState("all");
  const [filterDivision, setFilterDivision] = useState(user?.role === "ka_div" ? (user?.division || "all") : "all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const { updateTrigger } = useLiveUpdate();

  const fetchTasks = useCallback(async () => {
    try {
      const params = { page: currentPage, limit: 10 };
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterPriority !== "all") params.priority = filterPriority;
      if (filterProject !== "all") params.project = filterProject;
      if (filterAssignee !== "all") params.assignee_id = filterAssignee;
      if (filterDivision !== "all") params.division = filterDivision;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      
      const [tasksRes, usersRes, projectsRes] = await Promise.all([
        API.get("/tasks", { params }),
        API.get("/users"),
        API.get("/projects")
      ]);
      
      setTasks(tasksRes.data.data || tasksRes.data); // Support old & new API format
      setTotalItems(tasksRes.data.total || 0);
      setTotalPages(Math.ceil((tasksRes.data.total || tasksRes.data.length || 0) / 10));
      setUsers(usersRes.data);
      setProjects((projectsRes.data || []).map(p => p.name));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority, filterProject, filterAssignee, filterDivision, searchQuery, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterPriority, filterProject, filterAssignee, filterDivision, searchQuery]);

  useEffect(() => { fetchTasks(); }, [fetchTasks, updateTrigger]);

  const getUserName = (task) => {
    if (!task.assignees || task.assignees.length === 0) return "Belum Ditugaskan";
    return task.assignees.map(a => a.name).join(", ");
  };

  const isOverdue = (task) => {
    if (!task.deadline || task.status === "done") return false;
    return new Date(task.deadline) < new Date(new Date().toDateString());
  };

  // Developer can only edit/view tasks assigned to them
  const canEditTask = (task) => ["superadmin", "ka_div", "kepala_tik", "kapokja"].includes(user?.role) || (task.assignee_ids && task.assignee_ids.includes(user?.id)) || task.created_by === user?.id;

  const handleDelete = async (task) => {
    if (!window.confirm(`Hapus tugas "${task.title}"?`)) return;
    try {
      await API.delete(`/tasks/${task.id}`);
      toast.success("Tugas dihapus");
      fetchTasks();
    } catch {
      toast.error("Gagal menghapus tugas");
    }
  };

  const openEdit = (task) => {
    setEditTask(task);
    setDialogOpen(true);
  };

  const openCreate = () => {
    setEditTask(null);
    setDialogOpen(true);
  };

  const handleExportCSV = () => {
    if (tasks.length === 0) {
      toast.error("Tidak ada tugas untuk diekspor");
      return;
    }

    const headers = ["ID", "Judul", "Deskripsi", "Status", "Prioritas", "Tenggat Waktu", "Penugasan", "Dibuat Pada"];
    const rows = tasks.map(task => [
      task.id,
      `"${(task.title || "").replace(/"/g, '""')}"`,
      `"${(task.description || "").replace(/"/g, '""')}"`,
      task.status,
      task.priority,
      task.deadline || "Tanpa tenggat waktu",
      `"${(getUserName(task) || "").replace(/"/g, '""')}"`,
      new Date(task.created_at).toISOString().split('T')[0]
    ]);

    const csvContent = [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `ekspor_tugas_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Tugas diekspor ke CSV");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="tasks-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Tugas
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{totalItems > 0 ? `Total ${totalItems} tugas` : "Memuat tugas..."}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={handleExportCSV} variant="outline" className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700" data-testid="export-csv-button">
            <Download className="w-4 h-4 mr-2" /> Ekspor CSV
          </Button>
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-500 dark:hover:bg-blue-600" data-testid="create-task-button">
            <Plus className="w-4 h-4 mr-2" /> Tugas Baru
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6" data-testid="task-filters">
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400 dark:text-slate-500" />
          <Input
            placeholder="Cari tugas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 dark:text-slate-200 dark:placeholder-slate-400"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          <span className="text-sm text-slate-600 dark:text-slate-400 font-medium">Penyaring:</span>
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[140px] bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-md h-9" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="todo">To Do</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="review">Review</SelectItem>
            <SelectItem value="done">Selesai</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-[140px] bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-md h-9" data-testid="filter-priority">
            <SelectValue placeholder="Prioritas" />
          </SelectTrigger>
          <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
            <SelectItem value="all">Semua Prioritas</SelectItem>
            <SelectItem value="high">Tinggi</SelectItem>
            <SelectItem value="medium">Sedang</SelectItem>
            <SelectItem value="low">Rendah</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterProject} onValueChange={setFilterProject}>
          <SelectTrigger className="w-[140px] bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-md h-9" data-testid="filter-project">
            <SelectValue placeholder="Proyek" />
          </SelectTrigger>
          <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
            <SelectItem value="all">Semua Proyek</SelectItem>
            {projects.map(p => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterAssignee} onValueChange={setFilterAssignee}>
          <SelectTrigger className="w-[140px] bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-md h-9" data-testid="filter-assignee">
            <SelectValue placeholder="Penugasan" />
          </SelectTrigger>
          <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
            <SelectItem value="all">Semua Anggota</SelectItem>
            <SelectItem value="unassigned">Belum Ditugaskan</SelectItem>
            {users.map(u => (
              <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterDivision} onValueChange={setFilterDivision}>
          <SelectTrigger className="w-[170px] bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-md h-9">
            <SelectValue placeholder="Divisi" />
          </SelectTrigger>
          <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
            <SelectItem value="all">Semua Divisi</SelectItem>
            <SelectItem value="sistem_informasi">Sistem Informasi</SelectItem>
            <SelectItem value="infrastruktur_ti">Infrastruktur TI</SelectItem>
            <SelectItem value="pelayanan">Pelayanan</SelectItem>
            <SelectItem value="tata_kelola">Tata Kelola</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-none overflow-hidden">
        <Table data-testid="tasks-table">
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
              <TableHead className="p-4 font-semibold text-slate-700 dark:text-slate-300">Judul</TableHead>
              <TableHead className="p-4 font-semibold text-slate-700 dark:text-slate-300 hidden md:table-cell">Ditugaskan Kepada</TableHead>
              <TableHead className="p-4 font-semibold text-slate-700 dark:text-slate-300">Status</TableHead>
              <TableHead className="p-4 font-semibold text-slate-700 dark:text-slate-300 hidden sm:table-cell">Prioritas</TableHead>
              <TableHead className="p-4 font-semibold text-slate-700 dark:text-slate-300 hidden lg:table-cell">Tenggat Waktu</TableHead>
              <TableHead className="p-4 w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tasks.length === 0 ? (
              <TableRow className="border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <TableCell colSpan={6} className="text-center py-12 text-slate-500 dark:text-slate-400">
                  Tidak ada tugas ditemukan. Buat satu untuk memulai!
                </TableCell>
              </TableRow>
            ) : (
              tasks.map((task) => (
                <TableRow key={task.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700" data-testid={`task-row-${task.id}`}>
                  <TableCell className="p-4">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-slate-100 text-sm flex items-center gap-2">
                        {task.title}
                        {task.project && (
                          <span className="text-[10px] font-semibold bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400 px-1.5 py-0.5 rounded">
                            {task.project}
                          </span>
                        )}
                      </p>
                      {task.description && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">{task.description}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="p-4 hidden md:table-cell">
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      {task.assignees && task.assignees.length > 0 
                        ? task.assignees.map(a => a.name).join(", ") 
                        : "Belum Ditugaskan"}
                    </span>
                  </TableCell>
                  <TableCell className="p-4">
                    <Badge className={`${STATUS_STYLES[task.status]} text-xs font-medium`} data-testid={`task-status-${task.id}`}>
                      {STATUS_LABELS[task.status]}
                    </Badge>
                  </TableCell>
                  <TableCell className="p-4 hidden sm:table-cell">
                    <Badge className={`${PRIORITY_STYLES[task.priority]} text-xs font-medium capitalize`}>
                      {task.priority}
                    </Badge>
                  </TableCell>
                  <TableCell className="p-4 hidden lg:table-cell">
                    <span className={`text-sm ${isOverdue(task) ? "text-red-600 dark:text-red-400 font-medium" : "text-slate-600 dark:text-slate-400"}`}>
                      {task.deadline ? (
                        <span className="flex items-center gap-1">
                          {isOverdue(task) && <AlertTriangle className="w-3.5 h-3.5 text-red-500 dark:text-red-400" />}
                          {new Date(task.deadline).toLocaleDateString("id-ID")}
                          {isOverdue(task) && <span className="text-xs text-red-500 dark:text-red-400 ml-0.5">Terlambat</span>}
                        </span>
                      ) : "Tanpa tenggat"}
                    </span>
                  </TableCell>
                  <TableCell className="p-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 dark:text-slate-300 dark:hover:bg-slate-700" data-testid={`task-actions-${task.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="dark:bg-slate-800 dark:border-slate-700">
                        {canEditTask(task) ? (
                          <DropdownMenuItem onClick={() => openEdit(task)} className="dark:text-slate-200 dark:focus:bg-slate-700" data-testid={`edit-task-${task.id}`}>
                            <Pencil className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => openEdit(task)} className="dark:text-slate-200 dark:focus:bg-slate-700" data-testid={`view-task-${task.id}`}>
                            <Pencil className="w-4 h-4 mr-2" /> Lihat
                          </DropdownMenuItem>
                        )}
                        {["superadmin", "ka_div", "kepala_tik", "kapokja"].includes(user?.role) && (
                          <DropdownMenuItem onClick={() => handleDelete(task)} className="text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-300 dark:focus:bg-slate-700" data-testid={`delete-task-${task.id}`}>
                            <Trash2 className="w-4 h-4 mr-2" /> Hapus
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-2">
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Menampilkan <span className="font-medium">{(currentPage - 1) * 10 + 1}</span> hingga <span className="font-medium">{Math.min(currentPage * 10, totalItems)}</span> dari <span className="font-medium">{totalItems}</span> tugas
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="h-8 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Sebel
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-8 h-8 flex items-center justify-center rounded-md text-sm font-medium transition-colors
                    ${currentPage === page 
                      ? "bg-blue-600 text-white dark:bg-blue-500" 
                      : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
                >
                  {page}
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="h-8 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              Lanjut <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      <TaskDialog open={dialogOpen} onOpenChange={setDialogOpen} task={editTask} onSaved={fetchTasks} />
    </div>
  );
}
